import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { ProductType, ProductTypeOptions } from '@shopify-multiseller/graphql/partner/types';
import { SegmentedControl } from '../../../components/primitives';
import { useSettings } from '../../../hooks';
import { IProductInitialValuesProps } from '../types';

export interface ProductTypeFieldProps {
  disabled?: boolean;
  /** Physical products track stock, so the choice decides that too. */
  setTrackQuantity: (_value: boolean) => void;
  /**
   * Drop the field's own "Product type" label. Set when the field sits in a
   * panel of that name, where the label would say it twice.
   */
  hideLabel?: boolean;
}

/**
 * Whether the product is a physical thing or a download.
 *
 * Heads "Pricing & inventory", where the spike puts it: the choice decides
 * whether stock is tracked at all, so it belongs with the stock fields it
 * governs. Returns nothing when the shop's settings fix the type, since a
 * locked two-way toggle is a label pretending to be a control.
 */
export const ProductTypeField: React.FC<ProductTypeFieldProps> = ({
  disabled,
  setTrackQuantity,
  hideLabel,
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<IProductInitialValuesProps>();
  const settings = useSettings();
  const productTypeOption = settings.productType?.productType;

  // If the seller's settings force a single product type (not Both), default the
  // form's productType to it. Done in an effect via setFieldValue rather than
  // mutating Formik `values` during render (a React anti-pattern that also risked
  // the change not persisting through Formik).
  useEffect(() => {
    if (!values.productType && productTypeOption !== ProductTypeOptions.Both) {
      setFieldValue(
        'productType',
        productTypeOption === ProductTypeOptions.Physical ? ProductType.Physical : ProductType.Digital,
      );
    }
  }, [values.productType, productTypeOption]);

  if (productTypeOption !== ProductTypeOptions.Both) return null;

  const chooseProductType = (next: ProductType) => {
    setFieldValue('productType', next);
    setTrackQuantity(next === ProductType.Physical);
  };

  const typeError = touched.productType && errors.productType ? errors.productType : undefined;

  return (
    <div>
      {/*
        Two short, mutually exclusive choices that carry no configuration of
        their own: a segmented control states both at once in a single row.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span
            id="product-type-label"
            className={hideLabel ? 'sr-only' : 'text-sm font-medium text-foreground'}
          >
            Product type
          </span>
          {/* Both halves of the choice, as the design words it. Saying only
              what a physical product is leaves the reader to infer the other
              option by elimination, and "tracks stock" is a consequence they
              can see for themselves in the field below. */}
          <p className="mt-0.5 text-xs text-muted-foreground">
            Physical products are shipped; digital products deliver as a download.
          </p>
        </div>
        <SegmentedControl
          aria-labelledby="product-type-label"
          value={values.productType ?? undefined}
          disabled={disabled}
          onChange={chooseProductType}
          options={[
            { value: ProductType.Physical, label: 'Physical', testId: 'product-type-physical' },
            { value: ProductType.Digital, label: 'Digital', testId: 'product-type-digital' },
          ]}
        />
      </div>
      {typeError && <p className="mt-2 text-xs text-destructive first-letter:uppercase">{typeError}</p>}
    </div>
  );
};
